import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { getInteractions } from '../services/api';
import { Interaction } from '@redpanda/types';

interface InteractionState {
  // 每個客戶的互動資料快取 - key 是 customerId
  interactionsByCustomer: Record<string, Interaction[]>;
  
  // 載入狀態 - key 是 customerId
  loadingState: Record<string, boolean>;
  
  // Actions
  fetchInteractions: (customerId: string) => Promise<void>;
  getInteractionsFromCache: (customerId: string) => Interaction[] | null;
  addInteraction: (customerId: string, interaction: Interaction) => void;
  updateInteraction: (customerId: string, interaction: Interaction) => void;
  deleteInteraction: (customerId: string, interactionId: string) => void;
  clearCustomerInteractions: (customerId: string) => void;
}

export const useInteractionStore = create<InteractionState>()(
  persist(
    (set, get) => ({
      interactionsByCustomer: {},
      loadingState: {},

      fetchInteractions: async (customerId: string) => {
        set(state => ({
          loadingState: { ...state.loadingState, [customerId]: true }
        }));
        
        try {
          const response = await getInteractions(customerId);
          set(state => ({
            interactionsByCustomer: {
              ...state.interactionsByCustomer,
              [customerId]: response.data
            },
            loadingState: { ...state.loadingState, [customerId]: false }
          }));
        } catch (error) {
          set(state => ({
            loadingState: { ...state.loadingState, [customerId]: false }
          }));
          throw error;
        }
      },

      getInteractionsFromCache: (customerId: string) => {
        const state = get();
        return state.interactionsByCustomer[customerId] || null;
      },

      addInteraction: (customerId: string, interaction: Interaction) => {
        set(state => ({
          interactionsByCustomer: {
            ...state.interactionsByCustomer,
            [customerId]: [
              interaction,
              ...(state.interactionsByCustomer[customerId] || [])
            ]
          }
        }));
      },

      updateInteraction: (customerId: string, updatedInteraction: Interaction) => {
        set(state => ({
          interactionsByCustomer: {
            ...state.interactionsByCustomer,
            [customerId]: (state.interactionsByCustomer[customerId] || []).map(
              interaction => 
                interaction.id === updatedInteraction.id ? updatedInteraction : interaction
            )
          }
        }));
      },

      deleteInteraction: (customerId: string, interactionId: string) => {
        set(state => ({
          interactionsByCustomer: {
            ...state.interactionsByCustomer,
            [customerId]: (state.interactionsByCustomer[customerId] || []).filter(
              interaction => interaction.id !== interactionId
            )
          }
        }));
      },

      clearCustomerInteractions: (customerId: string) => {
        set(state => {
          const newInteractionsByCustomer = { ...state.interactionsByCustomer };
          delete newInteractionsByCustomer[customerId];
          return { interactionsByCustomer: newInteractionsByCustomer };
        });
      }
    }),
    {
      name: 'interaction-store',
      // 只持久化快取資料，不持久化載入狀態
      partialize: (state) => ({ 
        interactionsByCustomer: state.interactionsByCustomer 
      }),
    }
  )
);